import { z } from "zod";

// Common validation schemas
export const Email = z.string()
  .email({ message: "Please enter a valid email address" })
  .trim();

export const PhoneE164 = z.string()
  .regex(/^\+\d{6,15}$/, { message: "Please enter a valid phone number (e.g. +901234567890)" });

export const CurrencyISO = z.enum(["AED", "USD", "EUR", "GBP", "TRY"]);

export const Amount = z.number()
  .nonnegative({ message: "Amount must be zero or positive" });

// Password validation
export const Password = z.string()
  .min(8, { message: "Password must be at least 8 characters" })
  .regex(/(?=.*[a-z])/, { message: "Password must contain at least one lowercase letter" })
  .regex(/(?=.*[A-Z])/, { message: "Password must contain at least one uppercase letter" })
  .regex(/(?=.*\d)/, { message: "Password must contain at least one number" });

// SignUp Schema
export const SignUpSchema = z.object({
  role: z.enum(["EXHIBITOR", "CONTRACTOR", "SUPPLIER"]),
  org: z.object({
    display_name: z.string()
      .min(2, { message: "Company name must be at least 2 characters" }),
    country: z.string()
      .length(2, { message: "Country code must be 2 characters" }),
    city: z.string()
      .min(2, { message: "City name must be at least 2 characters" })
      .optional()
      .or(z.literal("")),
    website: z.string()
      .url({ message: "Please enter a valid website URL" })
      .optional()
      .or(z.literal(""))
  }),
  user: z.object({
    name: z.string()
      .min(2, { message: "Full name must be at least 2 characters" }),
    email: Email,
    phone_e164: PhoneE164,
    password: Password,
    confirmPassword: Password
  }),
  accept_terms: z.boolean()
    .refine(val => val === true, {
      message: "You must accept the terms and conditions"
    })
}).refine(data => data.user.password === data.user.confirmPassword, {
  message: "Passwords do not match",
  path: ["user", "confirmPassword"]
});

// Exhibitor Profile Schema
export const ExhibitorProfileSchema = z.object({
  activity: z.object({
    industries: z.array(z.string())
      .min(1, { message: "Please select at least one industry" }),
    typical_booth: z.object({
      w_m: z.number()
        .positive({ message: "Booth width must be positive" }),
      d_m: z.number()
        .positive({ message: "Booth depth must be positive" })
    }),
    shows_per_year: z.number()
      .int({ message: "Number of shows must be an integer" })
      .nonnegative({ message: "Number of shows must be zero or positive" }),
    annual_budget_min: Amount,
    annual_budget_max: Amount,
    currency: CurrencyISO
  }),
  primary_contact: z.object({
    name: z.string()
      .min(2, { message: "Contact name must be at least 2 characters" }),
    email: Email,
    phone_e164: PhoneE164
  })
}).refine(data => {
  return data.activity.annual_budget_max >= data.activity.annual_budget_min;
}, {
  message: "Maximum budget must be greater than or equal to minimum budget",
  path: ["activity", "annual_budget_max"]
});

// Contractor Profile Schema
export const ContractorProfileSchema = z.object({
  services: z.array(z.enum([
    "Design", "Build", "Rental", "Logistics", "AV", 
    "Electrical", "Rigging", "Graphics", "Furniture"
  ]))
  .min(1, { message: "Please select at least one service" }),
  territories: z.array(z.string())
    .min(1, { message: "Please select at least one territory" }),
  capabilities: z.object({
    max_booth_sqm: z.number()
      .int({ message: "Max booth area must be an integer" })
      .positive({ message: "Max booth area must be positive" }),
    avg_budget_min: Amount,
    avg_budget_max: Amount,
    currency: CurrencyISO,
    lead_time_weeks: z.number()
      .int({ message: "Lead time must be an integer" })
      .positive({ message: "Lead time must be positive" })
  }),
  portfolio: z.array(z.object({
    title: z.string()
      .min(2, { message: "Project title must be at least 2 characters" }),
    media: z.array(z.string())
      .min(1, { message: "At least one media file is required" })
  })).optional()
}).refine(data => {
  return data.capabilities.avg_budget_max >= data.capabilities.avg_budget_min;
}, {
  message: "Maximum budget must be greater than or equal to minimum budget",
  path: ["capabilities", "avg_budget_max"]
});

// Supplier Profile Schema
export const SupplierProfileSchema = z.object({
  catalog: z.object({
    categories: z.array(z.string())
      .min(1, { message: "Please select at least one category" }),
    items: z.array(z.object({
      sku: z.string()
        .min(2, { message: "SKU must be at least 2 characters" }),
      name: z.string()
        .min(2, { message: "Product name must be at least 2 characters" }),
      unit: z.enum(["piece", "m2", "meter", "day", "set"]),
      price: Amount,
      currency: CurrencyISO,
      images: z.array(z.string()).optional()
    }))
  })
});

// Common form schemas
export const ContactInfoSchema = z.object({
  name: z.string()
    .min(2, { message: "Full name must be at least 2 characters" }),
  email: Email,
  phone_e164: PhoneE164
});

export const AddressSchema = z.object({
  line1: z.string()
    .min(3, { message: "Address line must be at least 3 characters" }),
  city: z.string()
    .min(2, { message: "City name must be at least 2 characters" }),
  country: z.string()
    .length(2, { message: "Country code must be 2 characters" }),
  postal_code: z.string().optional()
});

// Brief Schema for Exhibitor Landing
export const BriefSchema = z.object({
  event: z.object({
    country: z.string().length(2, { message: "Country code must be 2 characters" }),
    city: z.string().min(2, { message: "City name must be at least 2 characters" }),
    show: z.string().min(2, { message: "Show name must be at least 2 characters" }),
    month: z.string().regex(/^\d{4}-\d{2}$/, { message: "Please enter a valid date format (YYYY-MM)" })
  }),
  stand: z.object({
    w_m: z.number().positive({ message: "Booth width must be positive" }),
    d_m: z.number().positive({ message: "Booth depth must be positive" }),
    area_sqm: z.number().positive().optional(),
    indoor: z.boolean()
  }),
  budget: z.object({
    min: z.number().nonnegative({ message: "Minimum budget must be zero or positive" }),
    max: z.number().nonnegative({ message: "Maximum budget must be zero or positive" }),
    currency: z.enum(["USD", "EUR", "GBP", "AED", "TRY"])
  }).refine(data => data.max >= data.min, {
    message: "Maximum budget must be greater than or equal to minimum budget",
    path: ["max"]
  }),
  deadline_at: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: "Please enter a valid date format (YYYY-MM-DD)" }),
  notes: z.string().max(2000, { message: "Notes can be at most 2000 characters" }).optional(),
  files: z.array(z.string()).optional()
});
